import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';

import {
  ArrowRight,
  CheckCircle,
  GearSix,
  Lock,
  MagicWand,
  UserPlus
} from '@phosphor-icons/react';
/**
 * AgentInstallSuccessModal - Phase 4 Updated
 * 
 * Now handles wizard auto-launch for voice agents and integration agents.
 * When an agent requires wizard setup, the primary CTA is "Configure Agent" 
 * which leads to the wizard.
 */
const AgentInstallSuccessModal = ({ 
  isOpen, 
  onClose, 
  agentName, 
  agentId,
  requiresWizard = false,
  wizardType = '',
  capabilityProfile = 'text_agent',
  onAssignAccess,
  onGoToAgent,
  onGoToWizard
}) => {
  // Determine CTA based on wizard requirement
  const showWizardCta = requiresWizard && capabilityProfile !== 'text_agent';
  
  // Get wizard description based on capability
  const getWizardDescription = () => {
    switch (capabilityProfile) {
      case 'live_voice_s2s_agent':
        return 'This voice agent needs configuration before it can take calls. The setup wizard will guide you through voice settings, Twilio, and optional calendar/SMS.';
      case 'voice_output_tts_agent':
        return 'This agent needs voice output configuration. The wizard will help you set up text-to-speech settings.';
      case 'voice_input_agent':
        return 'This agent needs voice input configuration. The wizard will help you set up speech-to-text settings.';
      case 'integration_automation_agent':
        return 'This agent requires integration connections. The wizard will guide you through connecting required services.';
      default:
        return 'This agent requires additional setup. The wizard will guide you through the configuration.';
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mx-auto mb-4">
            <CheckCircle className="w-6 h-6 text-green-600" />
          </div>
          <DialogTitle className="text-center text-xl">
            Agent Installed Successfully!
          </DialogTitle>
          <DialogDescription className="text-center">
            <strong>{agentName}</strong> has been added to your AI Workforce
          </DialogDescription>
        </DialogHeader>

        {/* Wizard Required Notice (Phase 4) */}
        {showWizardCta && (
          <Alert className="bg-purple-50 border-purple-200">
            <MagicWand className="h-4 w-4 text-purple-600" />
            <AlertDescription className="text-sm text-purple-900">
              <p className="font-medium mb-2">Guided Setup Required</p>
              <p className="text-xs">
                {getWizardDescription()}
              </p>
            </AlertDescription>
          </Alert>
        )}

        {/* Privacy-First Notice (only show when wizard not required) */}
        {!showWizardCta && (
          <Alert className="bg-blue-50 border-blue-200">
            <Lock className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-sm text-blue-900">
              <p className="font-medium mb-2">Privacy-first: assign this agent to users now</p>
              <p className="text-xs">
                This agent is available to <strong>admins only</strong> by default. 
                Assign access to employees when you're ready.
              </p>
            </AlertDescription>
          </Alert>
        )}

        {/* Actions */}
        <div className="space-y-2 mt-4">
          {showWizardCta ? (
            <>
              {/* Primary: Go to Wizard */}
              <Button
                onClick={onGoToWizard}
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
              >
                <MagicWand className="w-4 h-4 mr-2" />
                Configure Agent
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              
              {/* Secondary: Advanced Editor */}
              <Button
                onClick={onGoToAgent}
                className="w-full"
                variant="outline"
              >
                <GearSix className="w-4 h-4 mr-2" />
                Skip to Advanced Editor
              </Button>
            </>
          ) : (
            <>
              {/* Standard flow: Assign Access first */}
              <Button
                onClick={onAssignAccess}
                className="w-full"
                variant="default"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Assign access
              </Button>
              
              <Button
                onClick={onGoToAgent}
                className="w-full"
                variant="outline"
              >
                Go to Agent Editor
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}

          <Button
            onClick={onClose}
            className="w-full"
            variant="ghost"
          >
            Skip for now
          </Button>
        </div>

        {/* Footer Note */}
        <p className="text-xs text-center text-gray-500 mt-4">
          {showWizardCta 
            ? 'You can complete setup later from the Agent Editor'
            : 'You can assign access later from Organization → Team Members'
          }
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default AgentInstallSuccessModal;
